import type { ApiError } from '../api/client';
import type { LocationInput, LocationView, WorkingHours } from '../api/endpoints';
import { clockMinutes, isClockTime, normaliseClock } from './clock';
import { formatCoordinate, pointFromInputs } from './geometry';

/**
 * Форма локации — точки отправления (FR-501, FR-628): состояние, проверка, тело запроса.
 *
 * Чистые функции отдельно от панели, как у формы курьера: ошибки формы ловятся тестом,
 * а не на приёмке. Правила повторяют `LocationInputDto` сервера и схему `LocationInput`
 * контракта; клиентская проверка — подсказка человеку, сервер проверяет то же ещё раз.
 *
 * Пределы длины — ограничения схемы контракта, а не эксплуатационные параметры
 * (FR-417 их не касается).
 */

export const LOCATION_CODE_MAX = 64;
export const LOCATION_NAME_MAX = 200;
export const ADDRESS_MAX = 500;

export const LOCATION_TYPES = ['pharmacy', 'warehouse', 'store'] as const;
export type LocationKind = (typeof LOCATION_TYPES)[number];
/** Тип новой точки — первый в списке `LOCATION_TYPES` (тип локации, не инсталляции). */
const DEFAULT_LOCATION_TYPE: LocationKind = LOCATION_TYPES[0];

/** Дни недели ISO 8601: 1 — понедельник, 7 — воскресенье (схема `Location.working_hours`). */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export interface DayHours {
  readonly weekday: Weekday;
  readonly open: boolean;
  readonly opens_at: string;
  readonly closes_at: string;
}

export interface LocationFormState {
  readonly external_code: string;
  readonly type: LocationKind;
  readonly name: string;
  readonly address: string;
  /** Как в поле: строка, чтобы ввод `41,3` не превращался в число на полпути. */
  readonly lat: string;
  readonly lng: string;
  readonly zone_ids: readonly string[];
  readonly is_active: boolean;
  /** Часы не заданы вовсе — точка работает «как настроено в профиле», а не «закрыта». */
  readonly hours_enabled: boolean;
  readonly hours: readonly DayHours[];
}

export type LocationField = 'external_code' | 'name' | 'address' | 'point' | 'hours' | 'zone_ids';

/** Код ошибки поля — ключ в `messages.cabinet.locationForm.errors`. */
export type LocationFieldError =
  | 'codeRequired'
  | 'codeTooLong'
  | 'codeTaken'
  | 'nameRequired'
  | 'nameTooLong'
  | 'addressTooLong'
  | 'pointRequired'
  | 'hoursInvalid'
  | 'hoursEmpty'
  | 'zoneInvalid';

export type LocationFormErrors = Partial<Record<LocationField, LocationFieldError>>;

/** Часы по умолчанию для нового дня — то, что человек скорее всего и введёт. */
const DEFAULT_OPENS = '09:00';
const DEFAULT_CLOSES = '21:00';

function defaultHours(): DayHours[] {
  return WEEKDAYS.map((weekday) => ({
    weekday,
    open: true,
    opens_at: DEFAULT_OPENS,
    closes_at: DEFAULT_CLOSES,
  }));
}

export function emptyLocationForm(): LocationFormState {
  return {
    external_code: '',
    type: DEFAULT_LOCATION_TYPE,
    name: '',
    address: '',
    lat: '',
    lng: '',
    zone_ids: [],
    is_active: true,
    hours_enabled: false,
    hours: defaultHours(),
  };
}

/** Форма правки из записи списка. Неизвестный тип точки — тип по умолчанию. */
export function locationFormFrom(location: LocationView): LocationFormState {
  const point = location.point ?? location.address?.point ?? null;
  const stored = location.working_hours ?? [];
  const byDay = new Map(stored.map((item) => [item.weekday, item]));

  return {
    external_code: location.external_code,
    type: (LOCATION_TYPES as readonly string[]).includes(location.type)
      ? (location.type as LocationKind)
      : DEFAULT_LOCATION_TYPE,
    name: location.name,
    address: location.address?.raw ?? '',
    lat: formatCoordinate(point?.lat),
    lng: formatCoordinate(point?.lng),
    zone_ids: [...(location.zone_ids ?? [])],
    is_active: location.is_active,
    hours_enabled: stored.length > 0,
    hours: WEEKDAYS.map((weekday) => {
      const day = byDay.get(weekday);

      return day === undefined
        ? { weekday, open: false, opens_at: DEFAULT_OPENS, closes_at: DEFAULT_CLOSES }
        : {
            weekday,
            open: true,
            opens_at: normaliseClock(day.opens_at),
            closes_at: normaliseClock(day.closes_at),
          };
    }),
  };
}

/**
 * Проверка формы. `knownZones` — зоны, которые есть в справочнике: отмеченная, а потом
 * удалённая другим администратором зона иначе дала бы невнятный отказ сервера.
 */
export function validateLocationForm(
  form: LocationFormState,
  knownZones?: readonly string[],
): LocationFormErrors {
  const errors: LocationFormErrors = {};
  const code = form.external_code.trim();
  const name = form.name.trim();

  if (code === '') {
    errors.external_code = 'codeRequired';
  } else if (code.length > LOCATION_CODE_MAX) {
    errors.external_code = 'codeTooLong';
  }

  if (name === '') {
    errors.name = 'nameRequired';
  } else if (name.length > LOCATION_NAME_MAX) {
    errors.name = 'nameTooLong';
  }

  if (form.address.trim().length > ADDRESS_MAX) {
    errors.address = 'addressTooLong';
  }

  // Точка обязательна (FR-501): без неё нет ни подачи, ни ETA до точки забора.
  if (pointFromInputs(form.lat, form.lng) === null) {
    errors.point = 'pointRequired';
  }

  if (form.hours_enabled) {
    const open = form.hours.filter((day) => day.open);

    if (open.length === 0) {
      errors.hours = 'hoursEmpty';
    } else if (open.some((day) => !validDay(day))) {
      errors.hours = 'hoursInvalid';
    }
  }

  if (knownZones !== undefined && form.zone_ids.some((id) => !knownZones.includes(id))) {
    errors.zone_ids = 'zoneInvalid';
  }

  return errors;
}

/**
 * День открыт корректно, если оба времени — `HH:MM` и не совпадают. Закрытие раньше
 * открытия допустимо: это ночная смена через полночь (дежурная аптека 20:00–08:00).
 */
function validDay(day: DayHours): boolean {
  return (
    isClockTime(day.opens_at) &&
    isClockTime(day.closes_at) &&
    clockMinutes(day.opens_at) !== clockMinutes(day.closes_at)
  );
}

export function hasLocationErrors(errors: LocationFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Тело `POST /web/locations` и `PATCH /web/locations/{id}`.
 *
 * Тело — целиком и для правки: контракт ссылает `PATCH` на ту же схему с теми же
 * обязательными полями. Точка уходит дважды — в `point` (координата локации) и в
 * `address.point` (FR-505: координата источника приоритетнее геокодера и повторным
 * геокодированием не перезаписывается).
 *
 * `working_hours` в `LocationInput` контракта нет (принимает сервер), поэтому поле
 * уходит только по делу: часы заданы — списком; у точки были часы и их выключили
 * (`clearHours`) — пустым списком, иначе замена целиком оставила бы старые; иначе не
 * уходит вовсе. Вызывается после проверки формы — точка к этому моменту есть.
 */
export function toLocationInput(
  form: LocationFormState,
  options: { readonly clearHours?: boolean } = {},
): LocationInput {
  const point = pointFromInputs(form.lat, form.lng) ?? { lat: 0, lng: 0 };
  const address = form.address.trim();
  const hours: WorkingHours[] | null = form.hours_enabled
    ? form.hours
        .filter((day) => day.open)
        .map((day) => ({
          weekday: day.weekday,
          opens_at: day.opens_at.trim(),
          closes_at: day.closes_at.trim(),
        }))
    : options.clearHours === true
      ? []
      : null;

  return {
    external_code: form.external_code.trim(),
    type: form.type,
    name: form.name.trim(),
    ...(address === '' ? {} : { address: { raw: address, point } }),
    point,
    zone_ids: [...form.zone_ids],
    ...(hours === null ? {} : { working_hours: hours }),
    is_active: form.is_active,
  };
}

/** Часы понедельника — на все открытые дни: обычно точка работает одинаково всю неделю. */
export function copyFirstDay(hours: readonly DayHours[]): DayHours[] {
  const source = hours.find((day) => day.open) ?? hours[0];

  if (source === undefined) {
    return [...hours];
  }

  return hours.map((day) => ({
    ...day,
    open: true,
    opens_at: source.opens_at,
    closes_at: source.closes_at,
  }));
}

const SERVER_FIELDS: Readonly<Record<string, LocationField>> = {
  external_code: 'external_code',
  name: 'name',
  address: 'address',
  point: 'point',
  working_hours: 'hours',
  zone_ids: 'zone_ids',
};

const FIELD_FALLBACK: Readonly<Record<LocationField, LocationFieldError>> = {
  external_code: 'codeRequired',
  name: 'nameRequired',
  address: 'addressTooLong',
  point: 'pointRequired',
  hours: 'hoursInvalid',
  zone_ids: 'zoneInvalid',
};

/** Отказ сервера → ошибки по полям; остаток — общий блок с кодом для поддержки. */
export function locationErrorsFromProblem(error: ApiError): {
  readonly fields: LocationFormErrors;
  readonly general: boolean;
} {
  if (error.code === 'external_code_taken' || error.code === 'external_code_taken_location') {
    return { fields: { external_code: 'codeTaken' }, general: false };
  }

  const fields: LocationFormErrors = {};

  for (const item of error.problem.errors ?? []) {
    const field = SERVER_FIELDS[item.field.split('.')[0] ?? ''];

    if (field !== undefined) {
      fields[field] = FIELD_FALLBACK[field];
    }
  }

  return { fields, general: true };
}
